"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { useAuth } from "../../../context/AuthProvider";
import { useNavigate, useLocation } from "react-router-dom"
import { getInquiryByClientId, getInquiryStatusCount, getActiveStaff, getStaff, getStaffList, assignInquiryToStaff } from "../../../services/AdminService"
import { STATUS_MAP } from "../constants/statusMap"
import { FiFilter, FiCalendar, FiUsers, FiAlertCircle, FiChevronDown, FiInbox, FiLock, FiArrowRight, FiClock, FiMail, FiPhone, FiSearch } from "react-icons/fi"
import * as XLSX from "xlsx"

const INQUIRY_REFRESH_KEY = "inquiries_data_changed"
const ASSIGNED_USER_TAG_REGEX = /\[user_id:\d+\]/gi

// Safe text helper
function text(v, fallback = "-") {
  return v === undefined || v === null || v === "" ? fallback : v
}

function formatId(id) {
  if (!id) return "CBR-000"
  const numericId = String(id).replace(/\D/g, "")
  return `CBR-${numericId.padStart(3, "0")}`
}

function getInquiryRemarks(inquiry = {}) {
  return (
    inquiry?.agent_remarks ??
    inquiry?.agentRemarks ??
    inquiry?.Description ??
    inquiry?.description ??
    inquiry?.remarks ??
    ""
  )
}

function extractLatestAssignedUserIdFromRemarks(remarks = "") {
  const matches = [...String(remarks || "").matchAll(/\[user_id:(\d+)\]/gi)]
  if (matches.length === 0) return null

  const latestId = Number.parseInt(matches[matches.length - 1]?.[1], 10)
  return Number.isFinite(latestId) ? latestId : null
}

function getInquiryAssignedUserId(inquiry = {}) {
  const latestRemarksAssignedId = extractLatestAssignedUserIdFromRemarks(getInquiryRemarks(inquiry))
  if (Number.isFinite(latestRemarksAssignedId) && latestRemarksAssignedId > 0) {
    return latestRemarksAssignedId
  }

  const assignedId = Number.parseInt(inquiry?.assigned_to ?? inquiry?.assignedTo, 10)
  if (Number.isFinite(assignedId) && assignedId > 0) return assignedId

  return null
}

function buildRemarksWithAssignedUserTag(remarks = "", staffId) {
  const cleanRemarks = String(remarks || "").replace(ASSIGNED_USER_TAG_REGEX, "").trim()
  const normalizedStaffId = Number.parseInt(staffId, 10)

  if (!Number.isFinite(normalizedStaffId) || normalizedStaffId <= 0) {
    return cleanRemarks
  }

  return cleanRemarks
    ? `${cleanRemarks}\n[user_id:${normalizedStaffId}]`
    : `[user_id:${normalizedStaffId}]`
}

function extractErrorMessage(error, fallback = "Failed to load inquiries.") {
  if (!error) return fallback

  if (typeof error === "string" && error.trim()) return error

  const responseData = error.response?.data

  if (typeof responseData === "string" && responseData.trim()) {
    return responseData
  }

  if (responseData?.message) return responseData.message
  if (responseData?.error) return responseData.error
  if (error.message) return error.message

  return fallback
}

function isSuccessfulMutationResponse(response) {
  if (!response) return false
  if (response.status === true || response.success === true) return true

  const message = String(response.message || response.data?.message || "").toLowerCase()
  return message.includes("record with id") && message.includes("updated")
}

function isNoDataState(payloadOrError) {
  const status = payloadOrError?.response?.status
  const data = payloadOrError?.data ?? payloadOrError?.response?.data?.data
  const message =
    payloadOrError?.message ||
    payloadOrError?.error ||
    payloadOrError?.response?.data?.message ||
    payloadOrError?.response?.data?.error ||
    payloadOrError?.response?.data

  const normalized = String(message || "").toLowerCase()

  if (status === 404) return true
  if (Array.isArray(data) && data.length === 0 && !message) return true

  return (
    normalized.includes("no inquiries") ||
    normalized.includes("no inquiry") ||
    normalized.includes("no records") ||
    normalized.includes("no data") ||
    normalized.includes("not found")
  )
}

// =========================
// DATA MASKING HELPERS
// =========================

// Mask email: ti****40@wa****.com
function maskEmail(email) {
  if (!email || email === "-") return "-"

  const [user, domain] = email.split("@")
  if (!user || !domain) return "****@****"

  const maskedUser =
    user.length <= 4
      ? user[0] + "***"
      : user.slice(0, 2) + "****" + user.slice(-2)

  const domainParts = domain.split(".")
  const maskedDomain =
    domainParts[0].slice(0, 2) + "****." + domainParts.slice(1).join(".")

  return `${maskedUser}@${maskedDomain}`
}

// Mask phone: ******999
function maskContact(number) {
  if (!number || number === "-") return "-"

  const digits = number.replace(/\D/g, "")
  if (digits.length < 4) return "***"

  return "*".repeat(digits.length - 3) + digits.slice(-3)
}


function formatDateOnly(dateString) {
  if (!dateString) return "-"
  try {
    let date
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString
    }
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split("-")
      return `${year}-${month}-${day}`
    }
    date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return dateString
    }
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  } catch {
    return dateString
  }
}

function getDateOnlyValue(dateString) {
  if (!dateString) return ""
  const formatted = formatDateOnly(dateString)
  return formatted === "-" ? "" : formatted
}

// Fixed getDateRange to properly handle timezone for "Today" filter
function getDateRange(filterType) {
  const today = new Date()

  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, "0")
  const day = String(today.getDate()).padStart(2, "0")
  const todayString = `${year}-${month}-${day}`

  const startDate = new Date(todayString)
  const endDate = new Date(todayString)
  endDate.setDate(endDate.getDate() + 1)

  switch (filterType) {
    case "today":
      return { startDate, endDate }
    case "lastWeek":
      startDate.setDate(startDate.getDate() - 7)
      return { startDate, endDate }
    case "lastMonth":
      startDate.setMonth(startDate.getMonth() - 1)
      return { startDate, endDate }
    case "last3Months":
      startDate.setMonth(startDate.getMonth() - 3)
      return { startDate, endDate }
    default:
      return null
  }
}

export default function Inquires({ clientIdProp, pageSizeDefault = 10, isAgent = false }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { authReady, user } = useAuth();
  const abbreviation =
    user?.abbreviation ||
    localStorage.getItem("abbreviation") ||
    sessionStorage.getItem("abbreviation") ||
    ""
  const broadcastInquiryRefresh = useCallback(() => {
    if (typeof window === "undefined") return
    localStorage.setItem(INQUIRY_REFRESH_KEY, Date.now().toString())
  }, [])
  // =========================
  // EXPORT TO EXCEL
  // =========================
  const handleExportExcel = () => {
    if (!allInquiries.length) return

    const exportData = allInquiries.map((inquiry, index) => ({
      "Sr No": index + 1,
      "Inquiry ID": formatId(getId(inquiry)),
      "Client Name": getClientName(inquiry),
      "Email": getEmail(inquiry),
      "Contact": getContact(inquiry),
      "Status": text(getStatus(inquiry)),
      "Next Follow-up": formatDateOnly(getNextFollowup(inquiry)),
      "Created On": formatDateOnly(getCreatedAt(inquiry)),
      "Last Query": getLastQuestion(inquiry),
    }))

    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()

    XLSX.utils.book_append_sheet(workbook, worksheet, "Customer Inquiries")

    const fileName = `Customer_Inquiries_${new Date().toISOString().slice(0, 10)}.xlsx`
    XLSX.writeFile(workbook, fileName)
  }

  // Client ID resolution
  const [clientId, setClientId] = useState()

  useEffect(() => {
    if (!authReady) return

    const resolvedClientId =
      clientIdProp ||
      sessionStorage.getItem("client_id") ||
      sessionStorage.getItem("clientId") ||
      ""

    setClientId(resolvedClientId)

  }, [authReady, clientIdProp])
  // Data and UI state
  const [rawInquiries, setRawInquiries] = useState([])
  const [allInquiries, setAllInquiries] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(pageSizeDefault)
  const [totalRecords, setTotalRecords] = useState(0)
  const [statusFilter, setStatusFilter] = useState("all")
  const [statusCounts, setStatusCounts] = useState({})
  const [loadingCounts, setLoadingCounts] = useState(false)
  const [followupTodayOnly, setFollowupTodayOnly] = useState(false)

  const [staffList, setStaffList] = useState([])
  const [assigningInquiries, setAssigningInquiries] = useState(new Set())
  const [openAssignDropdownId, setOpenAssignDropdownId] = useState(null)

  const [dateFilter, setDateFilter] = useState("today")
  const [customStartDate, setCustomStartDate] = useState("")
  const [customEndDate, setCustomEndDate] = useState("")

  const lastFetchedFor = useRef(null)
  const hasFetchedRef = useRef(false)
  const initialLoadRef = useRef(true)
  const assignDropdownRefs = useRef({})
  useEffect(() => {
    if (location.state?.followupToday) {
      setFollowupTodayOnly(true)
      setPage(1)
    }
  }, [location.state])
  // Status count fetching
  const fetchStatusCounts = useCallback(async (cid, inquiriesArray = null) => {
    if (!cid) return

    if (isAgent && inquiriesArray) {
      const counts = {}
      Object.keys(STATUS_MAP).forEach(code => counts[code] = 0)
      inquiriesArray.forEach(inq => {
        const statusKey = (inq.status || "OPN").toUpperCase().trim()
        if (statusKey in counts) counts[statusKey]++
      })
      setStatusCounts(counts)
      return;
    }

    if (isAgent) return;

    setLoadingCounts(true)
    try {
      const response = await getInquiryStatusCount(cid)

      const counts = {}
      Object.keys(STATUS_MAP).forEach((code) => {
        counts[code] = 0
      })

      if (response && (response.status === true || response.success) && Array.isArray(response.data)) {
        response.data.forEach((item) => {
          if (item.status && item.status_count !== undefined) {
            const statusKey = item.status.toUpperCase().trim()
            if (statusKey in counts) {
              counts[statusKey] = item.status_count
            }
          }
        })
      }

      setStatusCounts(counts)
    } catch (e) {
      const defaultCounts = {}
      Object.keys(STATUS_MAP).forEach((code) => {
        defaultCounts[code] = 0
      })
      setStatusCounts(defaultCounts)
    } finally {
      setLoadingCounts(false)
    }
  }, [])

  // Staff fetching
  const fetchStaffData = useCallback(async (cid) => {
    if (!cid) return
    try {
      let staffData = []
      if (abbreviation === "T") {
        const result = await getActiveStaff(cid)
        if (result?.data?.data) {
          staffData = result.data.data
        }
      } else {
        const result = await getStaff()
        if (result?.data) {
          staffData = Object.values(result.data)
        } else {
          const alt = await getStaffList(cid)
          staffData = Array.isArray(alt) ? alt : []
        }
      }
      staffData = Array.isArray(staffData) ? staffData.filter((staff) => Number(staff?.status) === 1) : []
      setStaffList(staffData)
    } catch (error) {
      console.error("Error fetching staff:", error)
    }
  }, [abbreviation])

  // Assign agent handler
  const handleAssignAgent = async (inquiryId, staffId) => {
    const createdBy = parseInt(sessionStorage.getItem("userId")) || 0
    setAssigningInquiries((prev) => new Set([...prev, inquiryId]))
    setOpenAssignDropdownId(null)
    try {
      const inquiry = rawInquiries.find((item) => String(getId(item)) === String(inquiryId))
      const selectedStaff = staffList.find((staff) => String(staff.user_id) === String(staffId))
      const updatedRemarks = buildRemarksWithAssignedUserTag(getInquiryRemarks(inquiry), staffId)

      setRawInquiries((prev) =>
        prev.map((item) =>
          String(getId(item)) === String(inquiryId)
            ? {
              ...item,
              assigned_to: staffId,
              assignedTo: staffId,
              agent_remarks: updatedRemarks,
              agentRemarks: updatedRemarks,
              assignedName: selectedStaff?.user_name || item.assignedName || "Assigned",
            }
            : item
        )
      )

      const result = await assignInquiryToStaff({
        inquiryId,
        staffId,
        createdBy,
        currentStatus: inquiry?.status || inquiry?.status_code || "OPN",
        currentRemarks: updatedRemarks,
        nextFollowup: inquiry?.Next_followup || inquiry?.next_followup || inquiry?.nextFollowup || "",
      })

      if (isSuccessfulMutationResponse(result)) {
        broadcastInquiryRefresh()
      } else {
        setError(result?.message || "Failed to assign agent. Please try again.")
        if (clientId) fetchInquiries(clientId)
      }
    } catch (err) {
      console.error("Assignment failed:", err)
      setError("Assignment failed due to network error.")
      if (clientId) fetchInquiries(clientId)
    } finally {
      setAssigningInquiries((prev) => {
        const newSet = new Set(prev)
        newSet.delete(inquiryId)
        return newSet
      })
    }
  }

  // Date filtering
  const filterInquiriesByDate = (inquiries) => {
    let dateRange = null

    if (dateFilter === "custom") {
      if (!customStartDate || !customEndDate) return inquiries
      dateRange = {
        startDate: new Date(customStartDate),
        endDate: new Date(customEndDate),
      }
      dateRange.endDate.setDate(dateRange.endDate.getDate() + 1)
    } else {
      dateRange = getDateRange(dateFilter)
    }

    if (!dateRange) return inquiries

    return inquiries.filter((inquiry) => {
      const createdDateStr = getCreatedAt(inquiry)
      if (!createdDateStr || createdDateStr === "-") return false

      const createdDate = new Date(createdDateStr)
      if (isNaN(createdDate.getTime())) return false

      return createdDate >= dateRange.startDate && createdDate < dateRange.endDate
    })
  }

  // Fetch ALL inquiries (frontend pagination)
  const fetchInquiries = useCallback(
    async (cid) => {
      if (!cid) return
      setLoading(true)
      setError("")
      try {
        const response = await getInquiryByClientId(cid, 1, 1000)

        if (response && response.status) {
          let inquiries = Array.isArray(response.data) ? response.data : []

          if (isAgent && user?.userId) {
            inquiries = inquiries.filter((inq) => {
              const assignedToId = getInquiryAssignedUserId(inq)
              return parseInt(assignedToId, 10) === parseInt(user.userId, 10)
            });
            // Calculate Local Counters for Agent
            const counts = {}
            Object.keys(STATUS_MAP).forEach(code => counts[code] = 0)
            inquiries.forEach(inq => {
              const statusKey = (inq.status || "OPN").toUpperCase().trim()
              if (statusKey in counts) counts[statusKey]++
            })
            setStatusCounts(counts)
          }

          setRawInquiries(inquiries)

          // PROFESSIONAL DEFAULT BEHAVIOUR (FIRST LOAD ONLY)
          if (initialLoadRef.current && dateFilter === "today") {
            const todayRange = getDateRange("today")
            const todayCount = inquiries.filter((inq) => {
              const createdDateStr = getCreatedAt(inq)
              if (!createdDateStr || createdDateStr === "-") return false
              const createdDate = new Date(createdDateStr)
              if (isNaN(createdDate.getTime())) return false
              return createdDate >= todayRange.startDate && createdDate < todayRange.endDate
            }).length;

            if (todayCount === 0) {
              setDateFilter("last3Months");
            }
          }
          initialLoadRef.current = false;

        } else if (isNoDataState(response)) {
          setRawInquiries([]);
          setAllInquiries([]);
          setTotalRecords(0);
          setError("");
          initialLoadRef.current = false;
        } else {
          const backendMessage =
            response?.message ||
            response?.error ||
            response?.data?.message ||
            "Failed to load inquiries"

          console.error("Inquiry API returned unsuccessful response:", response)
          setRawInquiries([]);
          setAllInquiries([]);
          setError(backendMessage)
        }
      } catch (e) {
        console.error("Error fetching inquiries:", e?.response?.data || e)
        if (isNoDataState(e)) {
          setRawInquiries([]);
          setAllInquiries([]);
          setTotalRecords(0);
          setError("");
        } else {
          setError(extractErrorMessage(e))
          setRawInquiries([]);
          setAllInquiries([]);
        }
      } finally {
        setLoading(false)
      }
    },
    [dateFilter, isAgent, user?.userId]
  )

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!openAssignDropdownId) return

      const activeDropdown = assignDropdownRefs.current[openAssignDropdownId]
      if (activeDropdown && !activeDropdown.contains(event.target)) {
        setOpenAssignDropdownId(null)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [openAssignDropdownId])

  useEffect(() => {
    if (!authReady || !clientId) return

    const refreshFromSignal = () => {
      fetchInquiries(clientId)
      if (!isAgent) {
        fetchStatusCounts(clientId)
        fetchStaffData(clientId)
      }
    }

    const handleStorage = (event) => {
      if (event.key === INQUIRY_REFRESH_KEY) {
        refreshFromSignal()
      }
    }

    const handleFocus = () => {
      if (document.visibilityState === "hidden") return
      refreshFromSignal()
    }

    window.addEventListener("storage", handleStorage)
    window.addEventListener("focus", handleFocus)
    document.addEventListener("visibilitychange", handleFocus)

    return () => {
      window.removeEventListener("storage", handleStorage)
      window.removeEventListener("focus", handleFocus)
      document.removeEventListener("visibilitychange", handleFocus)
    }
  }, [authReady, clientId, fetchInquiries, fetchStatusCounts, fetchStaffData, isAgent])

  // Local filtering hook
  useEffect(() => {
    let filtered = [...rawInquiries];

    if (followupTodayOnly) {
      const today = getDateOnlyValue(new Date())
      filtered = filtered.filter((r) => {
        const status = (r?.status || "").toUpperCase().trim()
        if (!["OPN", "INP"].includes(status)) return false
        const followupDate = getDateOnlyValue(getNextFollowup(r))
        return followupDate === today
      })
    } else {
      if (statusFilter !== "all") {
        filtered = filtered.filter((r) => {
          const status = r?.status || ""
          return status === statusFilter
        })
      }

      filtered = filterInquiriesByDate(filtered)
    }

    setAllInquiries(filtered)
    setTotalRecords(filtered.length)
    // Don't override page if it triggers unwanted jumps, but setting page to 1 
    // happens natively when statusFilter changes dynamically inside the onClick
  }, [rawInquiries, statusFilter, dateFilter, customStartDate, customEndDate, followupTodayOnly])


  // Load inquiries and counts when clientId available
  useEffect(() => {
    if (!authReady) {
      return;
    }

    if (!clientId) {
      return;
    }

    if (hasFetchedRef.current && lastFetchedFor.current === clientId) {
      return; // ✅ STOP duplicate call
    }

    hasFetchedRef.current = true;
    lastFetchedFor.current = clientId;

    fetchInquiries(clientId);
    fetchStatusCounts(clientId);
    fetchStaffData(clientId);

  }, [clientId, fetchInquiries, fetchStatusCounts, fetchStaffData, authReady]);

  // Accessors
  const getId = (r) => r?.id ?? r?.ticket_id ?? r?.ticketId ?? "-"
  const getStatus = (r) => r?.status ?? ""
  const getClientName = (r) => r?.Client_name ?? r?.client_name ?? r?.clientName ?? "-"
  const getContact = (r) => r?.contact ?? r?.phone ?? r?.mobile ?? "-"
  const getEmail = (r) => r?.email ?? r?.Email ?? "-"
  const getLastQuestion = (r) => r?.last_question_text ?? r?.last_question ?? r?.lastQuestion ?? "-"
  const getAgentRemarks = (r) => text(getInquiryRemarks(r), "-")
  const getNextFollowup = (r) => r?.Next_followup ?? r?.next_followup ?? r?.nextFollowup ?? ""
  const getCreatedAt = (r) => r?.created_at ?? r?.createdAt ?? r?.created ?? "-"

  const handleView = (inquiry) => {
    const inquiryId = getId(inquiry)
    if (typeof window !== "undefined") {
      sessionStorage.setItem("selected_inquiry", JSON.stringify(inquiry))
    }
    const basePath = isAgent ? "/agent" : "/admin"
    navigate(`${basePath}/components/LeadStatus?inquiryId=${inquiryId}`)
  }

  // Pagination
  const totalPages = Math.ceil(totalRecords / pageSize)
  const startRecord = totalRecords === 0 ? 0 : (page - 1) * pageSize + 1
  const endRecord = Math.min(page * pageSize, totalRecords)
  const currentPageRecords = allInquiries.slice((page - 1) * pageSize, page * pageSize)
  const selectedDateLabel =
    followupTodayOnly
      ? "Follow-up today"
      : dateFilter === "today"
        ? "Today"
        : dateFilter === "lastWeek"
          ? "Last 7 Days"
          : dateFilter === "lastMonth"
            ? "Last 30 Days"
            : dateFilter === "last3Months"
              ? "Last 90 Days"
              : "Custom Range"
  const hasActiveFilters =
    statusFilter !== "all" ||
    dateFilter !== "today" ||
    (dateFilter === "custom" && (customStartDate || customEndDate))
  const showAssignedToColumn = !isAgent
  const STATUS_THEME = {
    OPN: { color: "#0ea5e9", bg: "#e0f2fe" },
    INP: { color: "#f59e0b", bg: "#fef3c7" },
    CRS: { color: "#22c55e", bg: "#dcfce7" },
    CNR: { color: "#ef4444", bg: "#fee2e2" },
    INV: { color: "#7c3aed", bg: "#ede9fe" },
    NIV: { color: "#6b7280", bg: "#f3f4f6" },
  }

  const getStatusPresentation = (inquiry) => {
    const statusCode = getStatus(inquiry)?.toUpperCase()
    const statusInfo =
      STATUS_MAP[statusCode] ||
      { label: text(getStatus(inquiry)), badge: "badge bg-secondary" }
    const theme = STATUS_THEME[statusCode] || { color: "#64748b", bg: "#f8fafc" }
    return { statusCode, statusInfo, theme }
  }

  const getAssignedPresentation = (inquiry) => {
    const assignedToId = parseInt(
      getInquiryAssignedUserId(inquiry) || 0
    )
    const assignedStaff = staffList.find((s) => parseInt(s.user_id) === assignedToId)
    const assignedName = assignedStaff ? assignedStaff.user_name : inquiry.assignedName || "Unassigned"
    const currentStatus = getStatus(inquiry)?.toUpperCase()
    const canAssign = currentStatus === "OPN" || currentStatus === "INP"
    return { assignedName, canAssign }
  }
  return (
    <div className="page-wrapper bg-light min-vh-100">
      <div className="container pt-1 pb-5">
        {/* Header */}
        <div className="inquiry-hero card border-0 shadow-sm mb-4">
          <div className="card-body p-3 p-md-4">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div>
                <div className="inquiry-kicker">Service workspace</div>
                <h2 className="mb-1 fw-semibold d-flex align-items-center gap-2 inquiry-hero-title">
                  <FiUsers className="text-primary" size={24} />
                  {isAgent ? "Assigned Inquiries" : "Lead Management"}
                </h2>
                <div className="text-muted inquiry-hero-copy">
                  Review the live pipeline, switch filters quickly, and open each record with a mobile-friendly workflow.
                </div>
              </div>
              <div className="inquiry-hero-meta">
                <div className="inquiry-hero-chip">
                  <FiSearch size={14} />
                  <span>{totalRecords} records</span>
                </div>
                <div className="inquiry-hero-chip">
                  <FiClock size={14} />
                  <span>{selectedDateLabel}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end mb-3">
          <div className="text-muted small">
            <span className="badge bg-primary-subtle text-primary fw-normal px-3 py-2 rounded-pill">
              Showing {startRecord} - {endRecord} of {totalRecords}
            </span>
          </div>
        </div>

        {followupTodayOnly && (
          <div className="alert alert-warning d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <FiAlertCircle />
              <span>Showing Open and In-Progress inquiries with follow-ups due today.</span>
            </div>
            <button
              className="btn btn-sm btn-outline-dark"
              onClick={() => setFollowupTodayOnly(false)}
            >
              Clear filter
            </button>
          </div>
        )}

        {/* Status Counts Cards */}
        <div className="row mb-4 g-3">
          {Object.entries(STATUS_MAP).map(([code, { label, icon }]) => {

            const theme =
              STATUS_THEME[code] || { color: "#2563eb", bg: "#f1f5f9" };

            return (
              <div key={code} className="col-12 col-sm-6 col-lg-2">
                <div
                  className={`card status-card h-100 ${statusFilter === code ? "active-status-card" : ""
                    }`}
                  style={{
                    borderTop: `4px solid ${theme.color}`,
                  }}
                  role="button"
                  onClick={() => {
                    setStatusFilter(code);
                    setDateFilter("last3Months");
                    setPage(1);
                  }}
                >
                  <div className="card-body text-center py-3">
                    <div
                      className="status-icon-wrapper mb-3 d-inline-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        backgroundColor: theme.bg,
                        color: theme.color,
                      }}
                    >
                      {icon}
                    </div>

                    <h6 className="card-title text-uppercase fw-semibold small mb-1">
                      {label}
                    </h6>

                    <p className="display-6 fw-bold mb-0">
                      {loadingCounts ? (
                        <span className="spinner-border spinner-border-sm text-primary" role="status"></span>
                      ) : (
                        statusCounts[code] ?? 0
                      )}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="card border-0 shadow-sm mb-4 rounded-4">
          <div className="card-body">
            <div className="d-flex align-items-center mb-3 gap-2">
              <FiFilter className="text-primary" />
              <span className="fw-semibold">Filters</span>
            </div>

            <div className="row g-3 align-items-end">
              {/* Date Filters */}
              <div className="col-12 col-lg-7">
                <div className="d-flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setDateFilter("today")
                      setPage(1)
                    }}
                    className={`btn btn-sm ${dateFilter === "today" ? "btn-primary" : "btn-outline-primary"
                      }`}
                  >
                    Today
                  </button>
                  <button
                    onClick={() => {
                      setDateFilter("lastWeek")
                      setPage(1)
                    }}
                    className={`btn btn-sm ${dateFilter === "lastWeek" ? "btn-primary" : "btn-outline-primary"
                      }`}
                  >
                    Last 7 Days
                  </button>
                  <button
                    onClick={() => {
                      setDateFilter("lastMonth")
                      setPage(1)
                    }}
                    className={`btn btn-sm ${dateFilter === "lastMonth" ? "btn-primary" : "btn-outline-primary"
                      }`}
                  >
                    Last 30 Days
                  </button>
                  <button
                    onClick={() => {
                      setDateFilter("last3Months")
                      setPage(1)
                    }}
                    className={`btn btn-sm ${dateFilter === "last3Months" ? "btn-primary" : "btn-outline-primary"
                      }`}
                  >
                    Last 90 Days
                  </button>
                  <button
                    onClick={() => {
                      setDateFilter("custom")
                      setPage(1)
                    }}
                    className={`btn btn-sm ${dateFilter === "custom" ? "btn-primary" : "btn-outline-primary"
                      } d-flex align-items-center gap-1`}
                  >
                    <FiCalendar size={14} />
                    Custom Range
                  </button>
                </div>

                {dateFilter === "custom" && (
                  <div className="row mt-3 g-2">
                    <div className="col-12 col-md-6">
                      <label className="form-label small text-muted mb-1">From</label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => {
                          setCustomStartDate(e.target.value)
                          setPage(1)
                        }}
                        className="form-control form-control-sm"
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label small text-muted mb-1">To</label>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => {
                          setCustomEndDate(e.target.value)
                          setPage(1)
                        }}
                        className="form-control form-control-sm"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Status Filter */}
              <div className="col-12 col-lg-3">
                <label className="form-label small text-muted mb-1">Status</label>
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-light border-0">
                    <FiAlertCircle className="text-muted" />
                  </span>
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value)
                      setPage(1)
                    }}
                    className="form-select border-start-0"
                  >
                    <option value="all">All Status</option>
                    {Object.entries(STATUS_MAP).map(([code, { label }]) => (
                      <option key={code} value={code}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Page size */}
              <div className="col-12 col-lg-2">
                <label className="form-label small text-muted mb-1">Rows per page</label>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value))
                    setPage(1)
                  }}
                  className="form-select form-select-sm"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="row mb-3">
            <div className="col-12">
              <div className="alert alert-danger alert-dismissible fade show shadow-sm" role="alert">
                <strong>Something went wrong.</strong> {error}
                <button type="button" className="btn-close" onClick={() => setError("")}></button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="card shadow-sm mb-4 border-0 rounded-4">
          <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center py-3 flex-wrap gap-2">
            <div>
              <h6 className="mb-0 fw-semibold text-dark">
                {isAgent ? "Assigned Inquiries" : "Customer Inquiries"}
              </h6>
              <small className="text-muted">
                Showing {startRecord} - {endRecord} of {totalRecords}
              </small>
            </div>
            <button className="btn btn-sm btn-light d-flex align-items-center gap-1" disabled>
              <FiCalendar size={14} />
              <span className="small text-muted text-uppercase">
                {dateFilter === "today"
                  ? "Today"
                  : dateFilter === "lastWeek"
                    ? "Last 7 Days"
                    : dateFilter === "lastMonth"
                      ? "Last 30 Days"
                      : dateFilter === "last3Months"
                        ? "Last 90 Days"
                        : "Custom Range"}
              </span>
            </button>
            {/* <button
              className="btn btn-sm btn-outline-success d-flex align-items-center gap-1"
              onClick={handleExportExcel}
            >
              📥 Export Excel
            </button> */}
          </div>

          <div className="d-lg-none p-3 border-top">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted mb-0">Loading inquiries, please wait...</p>
              </div>
            ) : currentPageRecords.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                  style={{
                    width: "64px",
                    height: "64px",
                    background: "#f8fafc",
                    color: "#64748b",
                  }}
                >
                  <FiInbox size={30} />
                </div>
                <div className="fw-semibold mb-2 fs-5 text-dark">
                  {hasActiveFilters ? "No inquiries match these filters" : "No inquiries yet"}
                </div>
                <div className="small text-muted mx-auto" style={{ maxWidth: 520 }}>
                  {hasActiveFilters
                    ? "Try widening the date range or changing the selected status to view available records."
                    : "This workspace is ready. New customer inquiries will appear here automatically once users start interacting with your bot."}
                </div>
              </div>
            ) : (
              <div className="mobile-inquiry-grid">
                {currentPageRecords.map((inquiry, idx) => {
                  const { statusInfo, theme } = getStatusPresentation(inquiry)
                  const { assignedName, canAssign } = getAssignedPresentation(inquiry)
                  const inqId = getId(inquiry)

                  return (
                    <article key={`mobile-${idx}`} className="mobile-inquiry-card">
                      <div className="mobile-inquiry-card-top">
                        <div>
                          <div className="mobile-inquiry-id">{formatId(getId(inquiry))}</div>
                          <h6 className="mb-1">{getClientName(inquiry)}</h6>
                        </div>
                        <span
                          className="mobile-status-pill"
                          style={{ color: theme.color, background: theme.bg }}
                        >
                          {statusInfo.label}
                        </span>
                      </div>

                      {getLastQuestion(inquiry) && (
                        <p className="mobile-inquiry-snippet">{getLastQuestion(inquiry)}</p>
                      )}

                      <div className="mobile-inquiry-meta">
                        <div className="mobile-inquiry-meta-item">
                          <FiMail size={14} />
                          <span className="masked">{maskEmail(getEmail(inquiry))}</span>
                        </div>
                        <div className="mobile-inquiry-meta-item">
                          <FiPhone size={14} />
                          <span className="masked">{maskContact(getContact(inquiry))}</span>
                        </div>
                        <div className="mobile-inquiry-meta-item">
                          <FiClock size={14} />
                          <span>Follow-up: {formatDateOnly(getNextFollowup(inquiry))}</span>
                        </div>
                      </div>

                      {showAssignedToColumn && (
                        <div className="mobile-inquiry-assignee">
                          {canAssign ? (
                            <div
                              className="position-relative"
                              ref={(element) => {
                                if (element) {
                                  assignDropdownRefs.current[inqId] = element
                                } else {
                                  delete assignDropdownRefs.current[inqId]
                                }
                              }}
                            >
                              <button
                                type="button"
                                className={`btn btn-sm dropdown-toggle ${assignedName === "Unassigned" ? "btn-outline-warning" : "btn-outline-primary"}`}
                                disabled={assigningInquiries.has(inqId)}
                                onClick={() =>
                                  setOpenAssignDropdownId((current) => (current === inqId ? null : inqId))
                                }
                              >
                                {assigningInquiries.has(inqId) ? (
                                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                ) : (
                                  assignedName
                                )}
                              </button>
                              <ul
                                className={`dropdown-menu ${openAssignDropdownId === inqId ? "show" : ""}`}
                                style={{ minWidth: "160px" }}
                              >
                                {staffList.length > 0 ? (
                                  staffList.map((staff) => (
                                    <li key={staff.user_id}>
                                      <button
                                        type="button"
                                        className={`dropdown-item ${getInquiryAssignedUserId(inquiry) === parseInt(staff.user_id, 10) ? "active" : ""}`}
                                        onClick={() => handleAssignAgent(inqId, staff.user_id)}
                                      >
                                        {staff.user_name}
                                      </button>
                                    </li>
                                  ))
                                ) : (
                                  <li>
                                    <span className="dropdown-item-text text-muted">No active staff available</span>
                                  </li>
                                )}
                              </ul>
                            </div>
                          ) : (
                            <div className="d-flex align-items-center text-muted small">
                              <FiLock className="me-1" />
                              <span>{assignedName}</span>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="mobile-inquiry-actions">
                        <span className="text-muted small">Created {formatDateOnly(getCreatedAt(inquiry))}</span>
                        <button
                          className="btn btn-sm btn-primary rounded-pill d-inline-flex align-items-center gap-2"
                          onClick={() => handleView(inquiry)}
                        >
                          Open
                          <FiArrowRight size={14} />
                        </button>
                      </div>
                    </article>
                  )
                })}
              </div>
            )}
          </div>

          <div className="table-responsive border-top d-none d-lg-block">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="fw-semibold text-muted small text-uppercase">ID</th>
                  <th className="fw-semibold text-muted small text-uppercase">Client</th>
                  <th className="fw-semibold text-muted small text-uppercase">Email</th>
                  <th className="fw-semibold text-muted small text-uppercase">Contact</th>
                  {showAssignedToColumn && <th className="fw-semibold text-muted small text-uppercase">Assigned To</th>}
                  <th className="fw-semibold text-muted small text-uppercase">Status</th>
                  <th className="fw-semibold text-muted small text-uppercase">Next Follow-up</th>
                  <th className="fw-semibold text-muted small text-uppercase">Created On</th>
                  <th className="fw-semibold text-muted small text-uppercase text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={showAssignedToColumn ? 9 : 8} className="text-center py-5">
                      <div className="d-flex flex-column align-items-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-muted mb-0">Loading inquiries, please wait…</p>
                      </div>
                    </td>
                  </tr>
                ) : currentPageRecords.length === 0 ? (
                  <tr>
                    <td colSpan={showAssignedToColumn ? 9 : 8} className="text-center py-5 text-muted">
                      <div className="d-flex flex-column align-items-center px-3">
                        <div
                          className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                          style={{
                            width: "64px",
                            height: "64px",
                            background: "#f8fafc",
                            color: "#64748b",
                          }}
                        >
                          <FiInbox size={30} />
                        </div>
                        <div className="fw-semibold mb-2 fs-5 text-dark">
                          {hasActiveFilters ? "No inquiries match these filters" : "No inquiries yet"}
                        </div>
                        <div className="small text-muted" style={{ maxWidth: 520 }}>
                          {hasActiveFilters
                            ? "Try widening the date range or changing the selected status to view available records."
                            : "This workspace is ready. New customer inquiries will appear here automatically once users start interacting with your bot."}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentPageRecords.map((inquiry, idx) => {
                    const statusInfo =
                      STATUS_MAP[getStatus(inquiry)?.toUpperCase()] ||
                      { label: text(getStatus(inquiry)), badge: "badge bg-secondary" }

                    const assignedToId = parseInt(
                      getInquiryAssignedUserId(inquiry) || 0
                    )
                    const assignedStaff = staffList.find((s) => parseInt(s.user_id) === assignedToId)
                    const assignedName = assignedStaff ? assignedStaff.user_name : (inquiry.assignedName || "Unassigned")
                    const currentStatus = getStatus(inquiry)?.toUpperCase()
                    const canAssign = currentStatus === "OPN" || currentStatus === "INP"
                    const inqId = getId(inquiry);

                    return (
                      <tr key={idx} className="align-middle">
                        <td className="fw-semibold text-primary small">
                          {formatId(getId(inquiry))}
                        </td>
                        <td>
                          <div className="fw-semibold">{getClientName(inquiry)}</div>
                          {getLastQuestion(inquiry) && (
                            <div className="small text-muted text-truncate" style={{ maxWidth: 260 }}>
                              {getLastQuestion(inquiry)}
                            </div>
                          )}
                        </td>
                        <td className="text-muted small masked">
                          {maskEmail(getEmail(inquiry))}
                        </td>

                        <td className="small masked">
                          {maskContact(getContact(inquiry))}
                        </td>
                        {showAssignedToColumn && (
                          <td>
                            {canAssign ? (
                              <div
                                className="position-relative d-inline-block"
                                ref={(element) => {
                                  if (element) {
                                    assignDropdownRefs.current[inqId] = element
                                  } else {
                                    delete assignDropdownRefs.current[inqId]
                                  }
                                }}
                              >
                                <button
                                  type="button"
                                  className={`btn btn-sm dropdown-toggle ${assignedName === "Unassigned" ? "btn-outline-warning" : "btn-outline-primary"}`}
                                  disabled={assigningInquiries.has(inqId)}
                                  style={{ minWidth: "70px" }}
                                  onClick={() =>
                                    setOpenAssignDropdownId((current) =>
                                      current === inqId ? null : inqId
                                    )
                                  }
                                >
                                  {assigningInquiries.has(inqId) ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                  ) : (
                                    assignedName
                                  )}
                                </button>
                                <ul
                                  className={`dropdown-menu ${openAssignDropdownId === inqId ? "show" : ""}`}
                                  style={{ minWidth: "160px" }}
                                >
                                  {staffList.length > 0 ? (
                                    staffList.map((staff) => (
                                      <li key={staff.user_id}>
                                        <button
                                          type="button"
                                          className={`dropdown-item ${getInquiryAssignedUserId(inquiry) === parseInt(staff.user_id, 10) ? "active" : ""}`}
                                          onClick={() => handleAssignAgent(inqId, staff.user_id)}
                                        >
                                          {staff.user_name}
                                        </button>
                                      </li>
                                    ))
                                  ) : (
                                    <li>
                                      <span className="dropdown-item-text text-muted">No active staff available</span>
                                    </li>
                                  )}
                                </ul>
                              </div>
                            ) : (
                              <div className="d-flex align-items-center text-muted">
                                <FiLock className="me-1" />
                                <span className="small">{assignedName}</span>
                              </div>
                            )}
                          </td>
                        )}
                        <td>
                          <span className={`badge px-3 py-2 ${statusInfo.badge || "bg-secondary"}`}>
                            {statusInfo.label}
                          </span>
                        </td>
                        <td className="text-muted small">
                          {formatDateOnly(getNextFollowup(inquiry))}
                        </td>
                        <td className="text-muted small">
                          {formatDateOnly(getCreatedAt(inquiry))}
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                            onClick={() => handleView(inquiry)}
                          >
                            View
                            <FiChevronDown size={14} className="rotate-90" />
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mb-3">
            <nav>
              <ul className="pagination justify-content-center mb-2">
                <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                  >
                    ← Previous
                  </button>
                </li>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                  const pageStart = (p - 1) * pageSize + 1
                  const pageEnd = Math.min(p * pageSize, totalRecords)
                  const label = `${pageStart}-${pageEnd}`

                  return (
                    <li key={p} className={`page-item ${page === p ? "active" : ""}`}>
                      <button
                        className="page-link"
                        onClick={() => setPage(p)}
                        title={`Records ${label}`}
                      >
                        {label}
                      </button>
                    </li>
                  )
                })}

                <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                  >
                    Next →
                  </button>
                </li>
              </ul>
            </nav>
            <div className="text-center text-muted small">
              Page {page} of {totalPages}
            </div>
          </div>
        )}
      </div>

      <style>{`
.page-wrapper {
  background: #f4f6f9;
}

.inquiry-hero {
  background:
    radial-gradient(circle at top left, rgba(37, 99, 235, 0.18), transparent 34%),
    radial-gradient(circle at bottom right, rgba(14, 165, 233, 0.16), transparent 32%),
    linear-gradient(145deg, #ffffff 0%, #f8fbff 100%);
  border-radius: 24px !important;
}

.inquiry-kicker {
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #2563eb;
}

.inquiry-hero-title {
  color: #0f172a;
}

.inquiry-hero-copy {
  max-width: 640px;
  line-height: 1.7;
}

.inquiry-hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.inquiry-hero-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #dbeafe;
  color: #334155;
  font-size: 0.84rem;
  font-weight: 600;
}

/* ================= STATUS CARDS ================= */

.status-card {
  border-radius: 16px;
  border: none;
  transition: all 0.25s ease;
  cursor: pointer;
  background: #ffffff;
  box-shadow: 0 6px 16px rgba(0,0,0,0.05);
  padding: 8px 0;
}

.status-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0,0,0,0.08);
}

.active-status-card {
  background: #f8fbff;
  box-shadow: 0 12px 28px rgba(37,99,235,0.15);
}

.status-card h6 {
  font-size: 12px;
  letter-spacing: 0.8px;
  font-weight: 600;
  color: #6b7280;
}

.status-card p {
  font-size: 32px;
  font-weight: 700;
  color: #111827;
}

/* ================= TABLE ================= */

.table thead th {
  font-size: 12px;
  letter-spacing: 0.6px;
  font-weight: 600;
  color: #6c757d;
}

.table tbody tr {
  transition: background 0.2s ease;
}

.table tbody tr:hover {
  background-color: #f8fafc;
}

.table td {
  font-size: 14px;
}

/* ================= FILTERS ================= */

.card {
  border-radius: 14px !important;
}

.btn-sm {
  border-radius: 8px !important;
}

.form-select,
.form-control {
  border-radius: 8px !important;
}

/* ================= ICONS ================= */

.status-icon-wrapper {
  width: 44px;
  height: 44px;
  font-size: 18px;
}

/* ================= MASKED ================= */

.masked {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  cursor: not-allowed;
  opacity: 0.8;
}

/* ================= PAGINATION ================= */

.pagination .page-link {
  border-radius: 8px !important;
}

.mobile-inquiry-grid {
  display: grid;
  gap: 14px;
}

.mobile-inquiry-card {
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  background: #fff;
  padding: 16px;
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.06);
}

.mobile-inquiry-card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.mobile-inquiry-id {
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #2563eb;
}

.mobile-status-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}

.mobile-inquiry-snippet {
  font-size: 0.9rem;
  color: #475569;
  margin-bottom: 12px;
}

.mobile-inquiry-meta {
  display: grid;
  gap: 8px;
  margin-bottom: 12px;
}

.mobile-inquiry-meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #475569;
  font-size: 0.86rem;
}

.mobile-inquiry-assignee {
  margin-bottom: 12px;
}

.mobile-inquiry-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

@media (max-width: 991.98px) {
  .row.mb-4.g-3 > .col-12.col-sm-6.col-lg-2 {
    flex: 0 0 50%;
    max-width: 50%;
  }
}

`}</style>
    </div>
  )
}
